import { motion } from "framer-motion";
import { Cpu, CheckCircle, Server, FileText, Shield, Sparkles } from "lucide-react";
import { DigitalEmployeeCard } from "./DigitalEmployeeCard";
import { BookingEmployeeCard } from "./BookingEmployeeCard";
import { DocumentEmployeeCard } from "./DocumentEmployeeCard";
import { CustomsEmployeeCard } from "./CustomsEmployeeCard";
import { SalesEmployeeCard } from "./SalesEmployeeCard";
import { DigitalWorkerCards } from "./DigitalWorkerCards";
import { useLanguage } from "@/contexts/LanguageContext";
import { AGENTS, AGENT_INDEX_INTRO } from "@/lib/agents";

export function DigitalWorkerShowcase() {
  const { t, language } = useLanguage();
  const lang = (language === "zh" || language === "th" ? language : "en") as
    | "en"
    | "zh"
    | "th";

  const rateHighlights = [
    t("rateWorker.h1"),
    t("rateWorker.h2"),
    t("rateWorker.h3"),
    t("rateWorker.h4"),
  ];

  const bookingHighlights = [
    t("bookingWorker.h1"),
    t("bookingWorker.h2"),
    t("bookingWorker.h3"),
    t("bookingWorker.h4"),
  ];

  const documentHighlights = [
    t("documentWorker.h1"),
    t("documentWorker.h2"),
    t("documentWorker.h3"),
    t("documentWorker.h4"),
  ];

  const customsHighlights = [
    t("customsWorker.h1"),
    t("customsWorker.h2"),
    t("customsWorker.h3"),
    t("customsWorker.h4"),
  ];

  const salesHighlights = [
    t("salesWorker.h1"),
    t("salesWorker.h2"),
    t("salesWorker.h3"),
    t("salesWorker.h4"),
  ];

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-500/5 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-cyan-500/5 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
            {t("digitalWorker.badge")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("digitalWorker.title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("digitalWorker.desc")}
          </p>
          <DigitalWorkerCards />
        </motion.div>

        <div className="sr-only">
          <p>{AGENT_INDEX_INTRO[lang]}</p>
          <ul>
            {AGENTS.map((agent) => (
              <li key={agent.id}>
                <a href={`#${agent.id}`}>
                  {agent.name} — {agent.role[lang]}
                </a>
                : {agent.duty[lang]}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-16" />

        {/* Rate Worker Section */}
        <div id="novarate" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-cyan-500 shadow-blue-500/25">
                <Cpu className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t("rateWorker.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("rateWorker.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("rateWorker.desc")}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {rateHighlights.map((highlight, idx) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-blue-500" />
                  <span className="text-foreground">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="px-5 py-3 rounded-xl inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-cyan-500">
                <span className="text-2xl font-bold text-primary-foreground">
                  {t("rateWorker.statValue")}
                </span>
                <span className="text-sm text-primary-foreground/80">
                  {t("rateWorker.statLabel")}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <DigitalEmployeeCard />
          </motion.div>
        </div>

        {/* Sales Worker (NovaSales) Section */}
        <div id="novasales" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-emerald-500/25">
                <Sparkles className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t("salesWorker.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("salesWorker.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("salesWorker.desc")}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {salesHighlights.map((highlight, idx) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-500" />
                  <span className="text-foreground">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="px-5 py-3 rounded-xl inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-cyan-500">
                <span className="text-2xl font-bold text-primary-foreground">
                  {t("salesWorker.statValue")}
                </span>
                <span className="text-sm text-primary-foreground/80">
                  {t("salesWorker.statLabel")}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <SalesEmployeeCard />
          </motion.div>
        </div>

        {/* Booking Worker Section */}
        <div id="novabook" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/25">
                <Server className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t("bookingWorker.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("bookingWorker.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("bookingWorker.desc")}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {bookingHighlights.map((highlight, idx) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-emerald-500" />
                  <span className="text-foreground">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="px-5 py-3 rounded-xl inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500">
                <span className="text-2xl font-bold text-primary-foreground">
                  {t("bookingWorker.statValue")}
                </span>
                <span className="text-sm text-primary-foreground/80">
                  {t("bookingWorker.statLabel")}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <BookingEmployeeCard />
          </motion.div>
        </div>

        {/* Document Worker Section */}
        <div id="novadocs" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-violet-500 to-purple-500 shadow-violet-500/25">
                <FileText className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t("documentWorker.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("documentWorker.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("documentWorker.desc")}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {documentHighlights.map((highlight, idx) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-violet-500" />
                  <span className="text-foreground">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="px-5 py-3 rounded-xl inline-flex items-center gap-3 bg-gradient-to-r from-violet-500 to-purple-500">
                <span className="text-2xl font-bold text-primary-foreground">
                  {t("documentWorker.statValue")}
                </span>
                <span className="text-sm text-primary-foreground/80">
                  {t("documentWorker.statLabel")}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <DocumentEmployeeCard />
          </motion.div>
        </div>

        {/* Customs Compliance Worker Section */}
        <div id="novacustoms" className="scroll-mt-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-purple-500/25">
                <Shield className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t("customsWorker.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("customsWorker.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("customsWorker.desc")}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {customsHighlights.map((highlight, idx) => (
                <motion.div
                  key={highlight}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-purple-500" />
                  <span className="text-foreground">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <div className="px-5 py-3 rounded-xl inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600">
                <span className="text-2xl font-bold text-primary-foreground">
                  {t("customsWorker.statValue")}
                </span>
                <span className="text-sm text-primary-foreground/80">
                  {t("customsWorker.statLabel")}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <CustomsEmployeeCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
